"use client";

import React, { useState, useEffect, useCallback } from "react";
import { useMediaQuery, Typography, Box } from "@mui/material";
import { getCookie, setCookie } from "cookies-next";
import ConfirmDialog from "@/app/components/modals/ConfirmDialog";
import { useTheme } from "@mui/material/styles";
import { track } from "@/lib/analytics/track";
import { EVENTS } from "@/lib/analytics/events";

import IosShareIcon from "@mui/icons-material/IosShare";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import MoreVertOutlinedIcon from "@mui/icons-material/MoreVertOutlined";
import PhoneAndroidOutlinedIcon from "@mui/icons-material/PhoneAndroidOutlined";

const InstallNotifier = ({ hasOtherNotifications = false }) => {
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalling, setIsInstalling] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    if (!isMobile) return;

    if (hasOtherNotifications) return;

    const userConfirmedInstall = getCookie("pwa-user-installed");
    if (userConfirmedInstall === "true") {
      return;
    }

    const snoozedUntil = getCookie("pwa-install-snoozed");
    if (snoozedUntil && parseInt(snoozedUntil) > Date.now()) {
      return;
    }

    const isAutoDetectedInstalled =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true ||
      document.referrer.includes("android-app://") ||
      window.matchMedia("(display-mode: fullscreen)").matches ||
      window.matchMedia("(display-mode: minimal-ui)").matches ||
      document.referrer.startsWith("android-app://") ||
      navigator.userAgent.includes("wv") ||
      window.location.search.includes("utm_source=homescreen") ||
      (window.navigator.userAgent.includes("iPhone") &&
        window.screen.height === window.innerHeight) ||
      (window.navigator.userAgent.includes("CriOS") &&
        window.innerHeight / window.screen.height > 0.95) ||
      window.location.href.includes("?source=pwa") ||
      document.title !== "Meex";

    if (isAutoDetectedInstalled) {
      setCookie("pwa-user-installed", "true", {
        maxAge: 60 * 60 * 24 * 90, // 3 months
        sameSite: "lax",
      });
      return;
    }

    const dismissedUntil = getCookie("pwa-install-dismissed");
    if (dismissedUntil && new Date() < new Date(dismissedUntil)) {
      return;
    }

    const remindLaterUntil = getCookie("pwa-install-remind-later");
    if (remindLaterUntil && new Date() < new Date(remindLaterUntil)) {
      return;
    }

    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallPrompt(true);
      track("pwa_install_prompt_shown", {
        trigger: "beforeinstallprompt",
      });
    };

    const handleAppInstalled = () => {
      setCookie("pwa-user-installed", "true", {
        maxAge: 60 * 60 * 24 * 365, // 1 year
        sameSite: "lax",
      });
      setShowInstallPrompt(false);
      setDeferredPrompt(null);
      track(EVENTS.PWA_INSTALLED, {
        trigger: "appinstalled_event",
      });
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);

    const fallbackTimer = setTimeout(() => {
      const isStillAutoDetected =
        window.matchMedia("(display-mode: standalone)").matches ||
        window.navigator.standalone === true ||
        document.referrer.includes("android-app://") ||
        (window.navigator.userAgent.includes("CriOS") &&
          window.innerHeight / window.screen.height > 0.95);

      if (isStillAutoDetected) {
        setCookie("pwa-user-installed", "true", {
          maxAge: 60 * 60 * 24 * 365, // 1 year
          sameSite: "lax",
        });
        return;
      }

      const userConfirmedCheck = getCookie("pwa-user-installed");
      if (userConfirmedCheck === "true") {
        return;
      }

      if (hasOtherNotifications) {
        return;
      }

      const isChrome =
        /Chrome/.test(navigator.userAgent) &&
        /Google Inc/.test(navigator.vendor);
      const isChromeiOS = /CriOS/.test(navigator.userAgent);
      const isEdge = /Edg/.test(navigator.userAgent);
      const isSafari =
        /Safari/.test(navigator.userAgent) &&
        !/Chrome/.test(navigator.userAgent);
      const isFirefox = /Firefox/.test(navigator.userAgent);

      if (
        (isChrome || isChromeiOS || isEdge || isSafari || isFirefox) &&
        !deferredPrompt &&
        !showInstallPrompt
      ) {
        setShowInstallPrompt(true);
      }
    }, 15000);

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt,
      );
      window.removeEventListener("appinstalled", handleAppInstalled);
      clearTimeout(fallbackTimer);
    };
  }, [isMobile, deferredPrompt, showInstallPrompt, hasOtherNotifications]);

  const handleInstall = async () => {
    if (deferredPrompt) {
      setIsInstalling(true);

      try {
        const promptResult = await deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;

        if (outcome === "accepted") {
          setCookie("pwa-user-installed", "true", {
            maxAge: 60 * 60 * 24 * 365, // 1 year
            sameSite: "lax",
          });
          setShowInstallPrompt(false);
        } else {
          handleDismiss();
        }
      } catch (error) {
        console.error("Install prompt failed:", error);
        handleDismiss();
      }

      setDeferredPrompt(null);
      setIsInstalling(false);
    } else {
      setShowInstructions(true);
    }
  };

  const handleDone = () => {
    setCookie("pwa-user-installed", "true", {
      maxAge: 60 * 60 * 24 * 365, // 1 year
      sameSite: "lax",
    });
    setShowInstallPrompt(false);
    setShowInstructions(false);
  };

  const handleRemindLater = () => {
    // Set cookie to remind later after 3 days
    const remindUntil = new Date();
    remindUntil.setDate(remindUntil.getDate() + 3);

    setCookie("pwa-install-remind-later", remindUntil.toISOString(), {
      expires: remindUntil,
      sameSite: "lax",
    });

    setShowInstallPrompt(false);
    setShowInstructions(false);
  };

  const handleDismiss = () => {
    const dismissUntil = new Date();
    dismissUntil.setMinutes(dismissUntil.getMinutes() + 3);

    setCookie("pwa-install-dismissed", dismissUntil.toISOString(), {
      expires: dismissUntil,
      sameSite: "lax",
    });

    setShowInstallPrompt(false);
    setShowInstructions(false);
  };

  const handleSnooze = useCallback(() => {
    const snoozeUntil = Date.now() + 24 * 60 * 60 * 1000;
    setCookie("pwa-install-snoozed", snoozeUntil.toString(), {
      maxAge: 60 * 60 * 24, // 24 hours
      sameSite: "lax",
    });
    setShowInstallPrompt(false);
    setShowInstructions(false);
  }, []);

  if (!isMobile || !showInstallPrompt || hasOtherNotifications) {
    return null;
  }

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isChromeiOS = /CriOS/.test(navigator.userAgent) && isIOS;

  const getInstallInstructions = () => {
    if (isChromeiOS) {
      return (
        <Box>
          <Box component="ol" sx={{ pl: 2, mb: 0 }}>
            <Typography
              component="li"
              variant="body1"
              sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}
            >
              <MoreVertOutlinedIcon fontSize="small" />
              Tap menu (⋮) in top right
            </Typography>
            <Typography
              component="li"
              variant="body1"
              sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}
            >
              <AddBoxOutlinedIcon fontSize="small" />
              Select &ldquo;Add to Home Screen&rdquo;
            </Typography>
            <Typography
              component="li"
              variant="body1"
              sx={{ mb: 0, display: "flex", alignItems: "center", gap: 1 }}
            >
              <CheckCircleOutlineOutlinedIcon fontSize="small" />
              Tap &ldquo;Add&rdquo;
            </Typography>
          </Box>
        </Box>
      );
    } else if (isIOS) {
      return (
        <Box>
          <Box component="ol" sx={{ pl: 2, mb: 0 }}>
            <Typography
              component="li"
              variant="body1"
              sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}
            >
              <IosShareIcon fontSize="small" />
              Tap Share button
            </Typography>
            <Typography
              component="li"
              variant="body1"
              sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}
            >
              <AddBoxOutlinedIcon fontSize="small" />
              Select &ldquo;Add to Home Screen&rdquo;
            </Typography>
            <Typography
              component="li"
              variant="body1"
              sx={{ mb: 0, display: "flex", alignItems: "center", gap: 1 }}
            >
              <CheckCircleOutlineOutlinedIcon fontSize="small" />
              Tap &ldquo;Add&rdquo;
            </Typography>
          </Box>
        </Box>
      );
    } else {
      // Android instructions
      return (
        <Box>
          <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
            To install Meex:
          </Typography>
          <Box component="ol" sx={{ pl: 2, mb: 0 }}>
            <Typography
              component="li"
              variant="body1"
              sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}
            >
              <MoreVertOutlinedIcon fontSize="small" />
              Tap browser menu
            </Typography>
            <Typography
              component="li"
              variant="body1"
              sx={{ mb: 0, display: "flex", alignItems: "center", gap: 1 }}
            >
              <PhoneAndroidOutlinedIcon fontSize="small" />
              Select &ldquo;Add to Home Screen&rdquo;
            </Typography>
          </Box>
        </Box>
      );
    }
  };

  return (
    <ConfirmDialog
      open={showInstallPrompt}
      onClose={handleDismiss}
      onConfirm={showInstructions ? handleDone : handleInstall}
      onSnooze={handleSnooze}
      title={showInstructions ? "Install instructions" : "Install Meex app"}
      body={
        showInstructions
          ? getInstallInstructions()
          : "Install Meex for faster access, and a native app experience on your device."
      }
      confirmButtonText={
        showInstructions
          ? "I've installed it"
          : isInstalling
            ? "Installing..."
            : "Install app"
      }
      showIgnoreButton={true}
      ignoreButtonText="Remind me later"
      onIgnore={handleRemindLater}
      showCloseIcon={true}
      isProcessing={isInstalling}
      disableBackdropClick={false}
      disableEscapeKeyDown={false}
      showSnoozeOption={true}
    />
  );
};

export default InstallNotifier;
